import axios, { AxiosResponse } from 'axios';
import { useMutation, useQueries, useQuery, UseQueryResult } from 'react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { addMinute, tzDate } from '@formkit/tempo';
import { addMinutes, setMinutes } from 'date-fns';

import {
  useAppSelector,
  selectWizardDatePicker,
  selectAccountFields,
  selectSaleAK,
  actions,
  useAppDispatch,
  selectIsCombo,
  selectComboIndex,
  selectFlow,
  selectWizard,
  selectWizardCurrentStep,
  selectCheckoutFlow,
  selectShopCartDate,
  selectPerformances,
  selectFirstTimeSlot,
} from '../../store';
import { getDateString, getMonthStartEndDate } from '../../utils/format-date';
import { useGeolocationQuery } from '../../utils';
import { formatShortDate } from '../../api/date/ny';
import { VIVA_CLIENT_BASE_URL } from '../../environment';
import { newRelic } from '../../utils/new-relic';

import {
  calendarAvailabilityMap,
  dataProviderResponseMap,
  findAllPerformanceByCategoriesResponseMap,
  isBosError,
  mapDayPerformances,
  putInArray,
  readAccountCategoryByAKResponseMap,
  responseMap,
  responseMapData,
  saveAccountResponseMap,
  searchAccountResponseMap,
  toIsoString,
} from './utils';
import {
  VivaAPIOperation,
  LangCode,
  AccountMask,
  SlugToBosEventMap,
  FlowToBosEventMap,
  BosEventCombos,
  ProductToPackageCode,
  FlowEvent,
  StatGroupByEvent,
  Flow,
  BosEvent,
  COMBO_FLOWS,
  ADDON_EVENTS_WITH_TIME_CAPACITY,
  CheckoutErrors,
  RINK_OR_RINK_MEMBERSHIP_RELATED,
  PerformanceCategory,
} from './constants';
import { CountryCodeDictionary } from './constants/country';

import type {
  SearchEventRequest,
  FindAllPerformanceProductByAkV2Request,
  CheckOutRequest,
  CloseOrderRequest,
  ReadOrderByAKRequest,
  ReadAccountCategoryByAKRequest,
  ReadDataProviderByCodeRequest,
  AbortSaleRequest,
  CheckBasketRequest,
  BOSProduct,
  ValidateCouponCodeRequest,
  PrintPdfTicketRequest,
  GetDaysAvailabilityRequest,
  GetDayPerformancesRequest,
  FindAllProductsByStatGroupRequest,
  SearchReservationRequest,
  SearchReservationResponse,
  DeleteReservationRequest,
  DeleteReservationResponse,
  SearchAccountRequest,
  FindAllPerformanceByCategoriesRequest,
} from './types/flows';
import type {
  OrderResponse,
  Shift4SaleResponse,
  Shift4ErrorResponse,
  BosErrorResponse,
  CheckBasketResponse,
  CheckOutResponse,
  CityPassCloseOrderResponse,
  CloseOrderResponse,
  Shift4VoidResponse,
  Shift4RefundResponse,
  CityPassVoucherValidationResponse,
} from './types/response';
import type {
  CheckoutCartSelectionCapacityGuidPayload,
  CheckoutOrderAccountFields,
  ShopCartSelectionFlow,
} from '../../store/types';
import type { RinkMember } from '../../blocks/BuyTicketsRinkMembershipAccountStepBlock';
import type { RefundRequest, SaleRequest } from './types/payments';
import type { Availability } from '../../components/BuyTickets/SegmentedTimePicker/Availabilities';

const baseURL = VIVA_CLIENT_BASE_URL || '/api';
const ax = axios.create({ baseURL });

export const getLastSlugSegment = (href?: string): string => {
  if (!href) {
    return '';
  }

  const segments = new URL(href).pathname.split('/');
  const last = segments.pop() || segments.pop(); // Handle potential trailing slash

  return last ?? '';
};

const useWindow = () => {
  const [window_, setWindow_] = useState<Window | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setWindow_(window);
    }
  }, []);

  return window_;
};

export const useCurrentEventByFlow = () => {
  const flow = useAppSelector(selectFlow);
  const [event, setEvent] = useState('');

  useEffect(() => {
    setEvent(FlowToBosEventMap[flow as keyof typeof FlowToBosEventMap] ?? '');
  }, [flow]);

  return event as BosEvent;
};

export const useFlowProductsQuery = (): UseQueryResult<{
  PRODUCTLIST?: { PRODUCT: BOSProduct[] };
}> => {
  const flow = useAppSelector(selectFlow);
  const window = useWindow();
  const flowSlug = getLastSlugSegment(window?.location?.href) as
    | keyof typeof SlugToBosEventMap
    | '';

  return useQuery(
    [VivaAPIOperation.FindAllProductByStatGroup, flowSlug],
    () => {
      return ax
        .post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.FindAllProductByStatGroup,
          parameters: { slug: flowSlug },
        })
        .then((data) => responseMapData(data));
    },
    { enabled: flowSlug !== '' },
  );
};

export const useFindAllProductByStatGroup = (
  parameters: FindAllProductsByStatGroupRequest,
): UseQueryResult<{
  PRODUCTLIST?: { PRODUCT: BOSProduct[] };
  status: { code: number };
}> => {
  const flow = useAppSelector(selectFlow);

  return useQuery(
    [VivaAPIOperation.FindAllProductByStatGroup, parameters.statGroups],
    () => {
      return ax
        .post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.FindAllProductByStatGroup,
          parameters,
        })
        .then((data) => responseMapData(data));
    },
    { enabled: parameters.enabled },
  );
};

// https://kb.bestunion.com/display/APIDoc/Error+Codes
/**
 * 2. SearchEvent
 * @param parameters { eventCategoryCodes: ['RRCRINK'] };
 * @returns UseQueryResult
 */
export const useSearchEventQuery = (parameters: SearchEventRequest) => {
  const flow = useAppSelector(selectFlow);
  return useQuery([VivaAPIOperation.SearchEvent], () => {
    return ax
      .post(`/viva/flows/reservation`, {
        flow,
        operation: VivaAPIOperation.SearchEvent,
        parameters,
      })
      .then((data) => responseMap(data, 'EVENT'));
  });
};

export const useGetDaysAvailabilityQuery = (
  {
    bosEvent,
    options,
    fetchNextMonth: fetchNextMonth,
    monthToFetch,
  }: {
    bosEvent?: BosEvent;
    options?: {
      enabled: boolean;
    };
    fetchNextMonth?: boolean;
    monthToFetch?: number;
  } = {
    options: { enabled: true },
    fetchNextMonth: false,
  },
) => {
  const eventFromFlow = useCurrentEventByFlow();
  const comboIndex = useAppSelector(selectComboIndex);
  const flow = useAppSelector(selectFlow);
  const isCombo = COMBO_FLOWS.includes(flow);
  const wizard = useAppSelector(selectWizard);
  const defaultCombo = BosEventCombos.RC_ROCK_PASS;

  const event =
    bosEvent && !isCombo
      ? [bosEvent]
      : isCombo
      ? [(BosEventCombos[flow] ?? defaultCombo!)[comboIndex]]
      : [eventFromFlow];

  // Get the default month range
  const { monthStart: from, monthEnd: to } = getMonthStartEndDate(
    wizard.datePicker.year,
    wizard.datePicker.month + 1,
  );

  const { monthStart: fromNextMonth, monthEnd: toNextMonth } =
    getMonthStartEndDate(wizard.datePicker.year, wizard.datePicker.month + 2);

  const filteredEvents = event.filter((event) => event !== undefined);

  // Special handling for ALL_IN_PASS
  const parameters: GetDaysAvailabilityRequest = {
    date: {
      from: formatShortDate(fetchNextMonth ? fromNextMonth : from),
      to: formatShortDate(fetchNextMonth ? toNextMonth : to),
    },
    events: filteredEvents,
  };

  const isEnabled =
    filteredEvents.length > 0 &&
    (options?.enabled ?? true) &&
    flow !== Flow.NONE;

  return useQuery(
    [
      VivaAPIOperation.GetDaysAvailability,
      parameters.date.from,
      parameters.date.to,
      event,
      monthToFetch,
    ],
    () => {
      return ax
        .post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.GetDaysAvailability,
          parameters,
        })
        .then(calendarAvailabilityMap);
    },
    {
      ...options,
      enabled: isEnabled,
    },
  );
};

export const useGetDaysAvailabilityQueries = (
  {
    bosEvents,
    options,
  }: {
    bosEvents?: BosEvent[];
    options?: {
      enabled: boolean;
      sellable?: boolean;
    };
  } = {
    options: { enabled: true },
  },
) => {
  const flow = useAppSelector(selectFlow);
  const wizard = useAppSelector(selectWizard);

  const { monthStart: from, monthEnd: to } = getMonthStartEndDate(
    wizard.datePicker.year,
    wizard.datePicker.month + 1,
  );

  const { sellable = true } = options ?? {};

  return useQueries(
    (bosEvents ?? []).map((bosEvent) => ({
      queryKey: [VivaAPIOperation.GetDaysAvailability, from, to, bosEvent],
      queryFn: async () => {
        const parameters: GetDaysAvailabilityRequest = {
          date: {
            from: formatShortDate(from),
            to: formatShortDate(to),
          },
          events: [bosEvent],
          sellable,
        };

        const res = await ax.post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.GetDaysAvailability,
          parameters,
        });

        return {
          bosEvent,
          availabilities: calendarAvailabilityMap(res),
        };
      },
      enabled: options?.enabled ?? true,
    })),
  );
};

const capacityTimeLogic = ({
  year,
  month,
  date,
  timeSlot,
  fromTime = 0,
  toTime = 60,
  startOfHour = false,
  previousHour = false,
}: {
  year: number;
  month: number;
  date: number;
  timeSlot: Availability;
  fromTime?: number;
  toTime?: number;
  startOfHour?: boolean;
  previousHour?: boolean;
}) => {
  const _fromTime = startOfHour ? 0 : fromTime;
  const dateObj = tzDate(new Date(), 'America/New_York');

  dateObj.setFullYear(year, month - 1, date);
  dateObj.setHours(
    previousHour ? timeSlot.startRaw.hours - 1 : timeSlot.startRaw.hours,
  );
  dateObj.setMinutes(startOfHour ? 0 : parseInt(timeSlot.startRaw.minutes));
  // Viva incorrectly uses the ISO string using the actual NY time.
  // We have to do the same to get the correct performances.
  const hours =
    dateObj.getHours() > 9 ? dateObj.getHours() : `0${dateObj.getHours()}`;
  dateObj.setHours(timeSlot.startRaw.hours);
  const toHours =
    dateObj.getHours() > 9 ? dateObj.getHours() : `0${dateObj.getHours()}`;
  const fromDate = `${hours}:${_fromTime > 9 ? _fromTime : '00'}:00`;
  const toDate =
    toTime >= 60 ? `${Number(toHours) + 1}:00:00` : `${toHours}:${toTime}:00`;

  return { fromDate, toDate };
};

export const useGetDayPerformancesQuery = (
  {
    date,
    from = '00:00:00.000Z',
    to = '23:59:59.000Z',
    bosEvent,
    options,
  }: {
    date?: string;
    from?: string;
    to?: string;
    bosEvent?: BosEvent;
    options: { enabled: boolean };
  } = {
    options: { enabled: true },
  },
) => {
  const isCombo = useAppSelector(selectIsCombo);
  const comboIndex = useAppSelector(selectComboIndex);
  const flow = useAppSelector(selectFlow);
  const eventFromFlow = useCurrentEventByFlow();
  const { day } = useAppSelector(selectShopCartDate);
  const timeSlot = useAppSelector(selectFirstTimeSlot);

  const isRockPass = eventFromFlow === 'combo-rock-pass';
  const defaultCombo = BosEventCombos.RC_ROCK_PASS!;

  const event = useMemo(() => {
    if (isRockPass) {
      return [(BosEventCombos[flow] ?? defaultCombo)[comboIndex]];
    } else {
      return [bosEvent ?? eventFromFlow];
    }
  }, [bosEvent, isRockPass, comboIndex, eventFromFlow]);

  const { month, year, time } = useAppSelector(selectWizardDatePicker);

  const selectedDate = getDateString(year, month, day);

  // if combo
  // add 1.5 hours to the time.
  const t = time > 0 && comboIndex > 0 ? time : new Date().setHours(0, 0, 0, 0);
  const isoTimeWithBuffer = `${addMinute(new Date(t), 90)
    .toISOString()
    .slice(11, 19)}Z`;

  const fromISO = isCombo ? isoTimeWithBuffer : from;
  const toISO = to;

  const parameters: GetDayPerformancesRequest = {
    date: date || selectedDate,
    time: {
      from: fromISO,
      to: toISO,
    },
    events: event,
  };

  return useQuery(
    [VivaAPIOperation.GetDayPerformances, comboIndex, day, event],
    () => {
      return ax
        .post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.GetDayPerformances,
          parameters,
        })
        .then(mapDayPerformances);
    },
    {
      ...options,
      enabled: !!event.length && options.enabled,
    },
  );
};

export const useFindAllPerformanceByCategoriesQuery = ({
  categories,
  date,
  options = { enabled: true },
}: {
  categories: string[];
  date: string;
  options?: { enabled: boolean };
}) => {
  const flow = useAppSelector(selectFlow);
  const parameters: FindAllPerformanceByCategoriesRequest = {
    date,
    categories,
  };

  return useQuery(
    [VivaAPIOperation.FindAllPerformanceByCategories, date, categories],
    () => {
      return ax
        .post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.FindAllPerformanceByCategories,
          parameters,
        })
        .then(findAllPerformanceByCategoriesResponseMap);
    },
    {
      ...options,
      enabled: options.enabled,
    },
  );
};

export const useFindAllPerformanceProductByAkMutation = () => {
  const selectedPerfAK = useAppSelector(selectPerformances);
  const flow = useAppSelector(selectFlow);

  return useMutation(
    async ({
      performanceAK,
      priceTableAK = '',
      statGroup,
    }: FindAllPerformanceProductByAkV2Request) => {
      const _statGroup = statGroup
        ? statGroup
        : StatGroupByEvent[FlowEvent[flow]];
      const perfAK = performanceAK ? performanceAK : selectedPerfAK[flow][0];

      return ax
        .post(`/viva/flows/reservation`, {
          flow,
          operation: VivaAPIOperation.FindAllPerformanceProductByAkV2,
          parameters: {
            performanceAK: perfAK,
            priceTableAK,
            statGroup: _statGroup,
          },
        })
        .then((data) => responseMap(data, 'PRODUCT'));
    },
  );
};

export const useGetDayPerformancesMutation = () => {
  const eventFromFlow = useCurrentEventByFlow();
  const orderComboIndex = useAppSelector(selectComboIndex);
  const flow = useAppSelector(selectFlow);
  const firstTimeSlot = useAppSelector(
    (state) => state.order.shopCart.timeSlot?.[0],
  );
  const currentStep = useAppSelector(selectWizardCurrentStep);

  let { month, year } = useAppSelector(selectWizardDatePicker);

  return useMutation(
    async ({
      bosEvent,
      day,
      fromTime = '00:00:00.000Z',
      toTime = '23:59:59.000Z',
      month: _month,
      year: _year,
      _comboIndex,
    }: {
      bosEvent?: BosEvent;
      day: number;
      fromTime?: string;
      toTime?: string;
      month?: number;
      year?: number;
      _comboIndex?: number;
    }) => {
      const comboIndex = _comboIndex ?? orderComboIndex;
      const _event = bosEvent ?? eventFromFlow;
      const isCombo = COMBO_FLOWS.includes(flow);

      let from = fromTime;
      let to = toTime;

      if (_month) month = _month - 1;
      if (_year) year = _year;

      const event = () => {
        if (isCombo && !bosEvent) {
          const defaultEvent = BosEventCombos.RC_ROCK_PASS!;
          if (currentStep === 3) {
            return [defaultEvent[0]] as BosEvent[];
          }
          return ([BosEventCombos[flow]?.[comboIndex]] ?? [
            defaultEvent[comboIndex],
          ]) as BosEvent[];
        } else {
          return [_event];
        }
      };
      // if combo
      // add 1.5 hours to the time.
      if (
        isCombo &&
        comboIndex > 0 &&
        event()[0] !== BosEvent.PHOTO_PACKAGE &&
        event()[0] !== BosEvent.CHAMPAGNE_TOAST &&
        event()[0] !== BosEvent.ORNAMENT
      ) {
        // Getting the date obj for time manipulation only
        const comboTimeObj = new Date();
        comboTimeObj.setHours(firstTimeSlot?.startRaw.hours ?? 0);
        comboTimeObj.setMinutes(
          parseInt(firstTimeSlot?.startRaw.minutes ?? '0'),
        );
        comboTimeObj.setSeconds(0);
        const futureComboTimeObj = addMinutes(comboTimeObj, 90);
        // Stitch together the expected format for the correct time to send to Viva
        const comboTime = `${futureComboTimeObj
          .getHours()
          .toString()
          .padStart(2, '0')}:${futureComboTimeObj
          .getMinutes()
          .toString()
          .padStart(2, '0')}:00.000Z`;
        from = comboTime;
      } else if (
        bosEvent &&
        ADDON_EVENTS_WITH_TIME_CAPACITY.includes(bosEvent) &&
        firstTimeSlot
      ) {
        const fromTimeOffset = () => {
          if (
            (flow === Flow.RC_ROCK_PASS ||
              flow === Flow.TOR_VIP_ROCKSTAR ||
              Flow.RC_ROCK_PASS_VIP_HOLIDAY) &&
            bosEvent === BosEvent.THE_BEAM
          ) {
            return 90;
          } else if (bosEvent === BosEvent.TREE_PHOTO) {
            return 30;
          }
          return 15;
        };

        // Returns the time for the beginning of the next half hour.
        const getNextHalfHourTime = (
          timeSlot: typeof firstTimeSlot,
        ): string => {
          if (!timeSlot) return '';

          const date = new Date(timeSlot.timeRaw);
          const minutes = date.getMinutes();

          let nextHalfHour;
          if (minutes < 30) {
            nextHalfHour = setMinutes(date, 30);
          } else {
            nextHalfHour = setMinutes(addMinutes(date, 60), 0);
          }

          return nextHalfHour.toISOString().slice(11, 16);
        };

        const previousHour = bosEvent === BosEvent.TREE_PHOTO;
        let toTime = bosEvent === BosEvent.TREE_PHOTO ? 9 : 59;

        // If this is the bunny photo, we go up a full hour
        if (bosEvent === BosEvent.BUNNY_PHOTO) {
          toTime++;
        }
        let fromDate = '';
        let toDate = '';

        if (bosEvent === BosEvent.SANTA_PHOTO) {
          const nextHalfHourTime = getNextHalfHourTime(firstTimeSlot);
          fromDate = `${nextHalfHourTime}:00.000Z`;
          toDate = `23:59:59.000Z`; // Set to end of day
        } else {
          const { fromDate: _fromDate, toDate: _toDate } = capacityTimeLogic({
            year,
            month,
            date: day,
            timeSlot: firstTimeSlot,
            fromTime: fromTimeOffset(),
            toTime,
            startOfHour: true,
            previousHour,
          });

          fromDate = _fromDate;
          toDate = _toDate;
        }

        from = fromDate;
        to = toDate;
      }
      const parameters: GetDayPerformancesRequest = {
        date: getDateString(year, month, day),
        time: {
          from,
          to,
        },
        events: event(),
      };

      const data = await ax.post(`/viva/flows/reservation`, {
        flow,
        operation: VivaAPIOperation.GetDayPerformances,
        parameters,
      });

      return mapDayPerformances(data);
    },
  );
};

/**
 * 4. FindAllPerformanceProductByAkV2
 * @param parameters { performanceAK: 'TOR.EVN4.PRF5854' }
 * @returns UseQueryResult
 */
export const useFindAllPerformanceProductByAkV2Query = (
  parameters: FindAllPerformanceProductByAkV2Request,
) => {
  const { performanceAK, priceTableAK = '', statGroup } = parameters;

  // We get the hour and flow here to call this query for new products
  // every hour rather than for every performance.
  // TODO : remove?
  // const performanceHour = new Date(performanceTime).getHours();
  const selectedPerfAK = useAppSelector(selectPerformances);
  const flow = useAppSelector(selectFlow);
  const _statGroup = statGroup ? statGroup : StatGroupByEvent[FlowEvent[flow]];
  const perfAK = performanceAK ? performanceAK : selectedPerfAK[flow][0];
  const qkPerformanceAK = flow === Flow.RINK ? perfAK : '';

  return useQuery(
    [
      VivaAPIOperation.FindAllPerformanceProductByAkV2,
      priceTableAK,
      flow,
      qkPerformanceAK,
    ],
    ({ signal }) => {
      return ax
        .post(
          `/viva/flows/reservation`,
          {
            flow,
            operation: VivaAPIOperation.FindAllPerformanceProductByAkV2,
            parameters: {
              ...parameters,
              performanceAK: perfAK,
              statGroup: _statGroup,
            },
          },
          { signal },
        )
        .then((data) => responseMap(data, 'PRODUCT'));
    },
    { enabled: Boolean(perfAK) },
  );
};

/**
 * 5. ReadAccountCategoryByAK
 * @param parameters { ADmgCategoryAK: 'TOR.DMGCAT45'; }
 * @returns UseQueryResult
 */
export const useReadAccountCategoryByAKQuery = (
  parameters: ReadAccountCategoryByAKRequest,
) => {
  const flow = useAppSelector(selectFlow);
  return useQuery([VivaAPIOperation.ReadAccountCategoryByAK], async () => {
    const data = await ax.post(`/viva/flows/reservation`, {
      flow,
      operation: VivaAPIOperation.ReadAccountCategoryByAK,
      parameters,
    });

    return readAccountCategoryByAKResponseMap(data);
  });
};

/**
 * 6-1. SearchAccount
 * @param parameters { email: string; }
 * @returns UseQueryResult
 */
export const useSearchAccountMutation = () => {
  return useMutation(async (parameters: SearchAccountRequest) => {
    const data = await ax.post(`/viva/flows/reservation`, {
      operation: VivaAPIOperation.SearchAccount,
      parameters,
    });

    return searchAccountResponseMap(data);
  });
};

/**
 * 6-2. SaveAccount
 * @returns UseQueryResult
 */
export const useSaveAccountMutation = () => {
  const accountFields = useAppSelector(selectAccountFields);
  const geolocationQuery = useGeolocationQuery();
  const dispatch = useAppDispatch();

  return useMutation(
    async ({
      flow = Flow.CHECKOUT,
      accountAK,
    }: {
      flow: Flow;
      accountAK?: string;
    }) => {
      const fields = [
        {
          type: AccountMask.Country,
          value: accountFields?.address.country ?? 'USA',
        },
        {
          type: AccountMask.FirstName,
          value: accountFields?.firstName,
        },
        {
          type: AccountMask.LastName,
          value: accountFields?.lastName,
        },
        {
          type: AccountMask.Email,
          value: accountFields?.email,
        },
        {
          type:
            flow === Flow.RINK_MEMBERSHIP
              ? AccountMask.RinkMembershipMobileNumber
              : AccountMask.MobileNumber,
          value: accountFields?.mobileNumber,
        },
        {
          type: AccountMask.Address1,
          value: accountFields?.address.street,
        },
        {
          type: AccountMask.State,
          value: accountFields?.address.state,
        },
        {
          type: AccountMask.City,
          value: accountFields?.address.city,
        },
        {
          type: AccountMask.ZipCode,
          value: accountFields?.address.zip,
        },
        {
          type: AccountMask.OptInMail,
          value: accountFields?.optInMail ?? 'false',
        },
        {
          type: AccountMask.OptInSMS,
          value: accountFields?.optInSMS ?? 'false',
        },
        { type: AccountMask.IPAddress, value: geolocationQuery.data?.IPv4 },
      ];

      interface ErrorField {
        type: number;
        value: string;
      }

      const findValueByType = (
        arr: ErrorField[],
        typeToFind: number,
      ): string | null => {
        const found = arr.find((item) => item.type === typeToFind);
        return found?.value ?? null;
      };

      const data = await ax.post(`/viva/flows/reservation`, {
        flow,
        operation: VivaAPIOperation.SaveAccount,
        parameters: { accountAK, flow, fields },
      });

      if (data.data?.status?.code !== 200) {
        const valuesWithError = putInArray(
          data.data.ERRORLISTOBJTYPE.ERRORLIST,
        ).map((errorItem) => {
          let fields: ErrorField[] = [];

          try {
            const parsedData = JSON.parse(data.config.data);
            fields = parsedData?.parameters?.fields ?? [];
          } catch (e) {
            console.error('Failed to parse fields data:', e);
            newRelic.logCartAction(
              'PaymentFormViva - SaveAccount Parse Error',
              'useSaveAccountMutation',
              { error: e },
            );
          }

          const fieldValueWithError = findValueByType(
            fields,
            Number(errorItem.OBJTYPE),
          );

          return {
            OBJTYPE: errorItem.OBJTYPE,
            fieldValueWithError: fieldValueWithError ?? 'OBJTYPE not found',
          };
        });

        newRelic.logCartAction(
          'PaymentFormViva - SaveAccount Error',
          'useSaveAccountMutation',
          {
            status: data.data.status.code,
            ERROR: data.data.ERROR,
            ERRORLIST: data.data.ERRORLISTOBJTYPE.ERRORLIST,
            valuesWithError,
          },
        );

        const err = new Error(data.data?.ERROR?.TEXT);
        err.name = CheckoutErrors.AccountError;
        throw err;
      }

      return saveAccountResponseMap(data);
    },
    {
      onSuccess: (data) => {
        // WARN: This is a side effect
        dispatch(actions.order.setAccountAK(data.ACCOUNTAK));
      },
    },
  );
};

/**
 * 6b. Rink Memberships get one account per membership
 * @returns UseQueryResult
 **/

export const useSaveRinkMembershipAccountMutation = () => {
  const accountFields = useAppSelector(selectAccountFields);
  const geolocationQuery = useGeolocationQuery();

  return useMutation(async ({ member }: { member: RinkMember }) => {
    const fields = [
      {
        type: AccountMask.Country,
        value: accountFields?.address.country,
      },
      {
        type: AccountMask.FirstName,
        value: member.firstName,
      },
      {
        type: AccountMask.LastName,
        value: member.lastName,
      },
      {
        type: AccountMask.Email,
        value: member.email,
      },
      {
        type: AccountMask.RinkMembershipMobileNumber,
        value: member.phoneNumber,
      },
      {
        type: AccountMask.Address1,
        value: accountFields?.address.street,
      },
      {
        type: AccountMask.State,
        value: accountFields?.address.state,
      },
      {
        type: AccountMask.City,
        value: accountFields?.address.city,
      },
      {
        type: AccountMask.ZipCode,
        value: accountFields?.address.zip,
      },
      // I'm assuming since there isn't a checkbox for this,
      // and because these members are possibly not present at checkout
      // to give consent, it should be false
      {
        type: AccountMask.OptInMail,
        value: 'false',
      },
      {
        type: AccountMask.OptInSMS,
        value: 'false',
      },
      {
        type: AccountMask.DOB,
        value: `${member.birthMonth}-${member.birthDate}-${member.birthYear}`,
      },
      {
        type: AccountMask.IPAddress,
        value: geolocationQuery.data?.IPv4,
      },
    ];

    const data = await ax.post(`/viva/flows/reservation`, {
      flow: Flow.RINK_MEMBERSHIP,
      operation: VivaAPIOperation.SaveAccount,
      parameters: { fields },
    });

    const output: {
      ACCOUNTAK?: string;
    } = saveAccountResponseMap(data);

    return output;
  });
};

/**
 * 7. CheckOut
 * @param parameters {
      items: [ {
        AK: 'TOR.EVN1.MCC209',
        QTY: 1,
        performances: ['TOR.EVN4.PRF5854'],
      } ],
      accountAK: '13700822000034',
    }
 * @returns UseQueryResult
 */
export const useCheckOutMutation = () => {
  const determinedFlow = useAppSelector(selectCheckoutFlow);

  return useMutation<CheckOutResponse, BosErrorResponse, CheckOutRequest>(
    async (parameters) => {
      const data = await ax.post<BosErrorResponse, CheckOutResponse>(
        `/viva/flows/reservation`,
        {
          flow: determinedFlow,
          operation: VivaAPIOperation.CheckOut,
          parameters,
        },
      );
      const response = responseMapData(data) as CheckOutResponse;

      if (response.ERROR.CODE !== '200') {
        const err = new Error(response.ERROR.TEXT);
        err.name = CheckoutErrors.CheckoutError;
        throw err;
      }

      return response;
    },
  );
};

export const useReadPerformanceByAKMutation = (flow: Flow) => {
  return useMutation(async (performanceAK: string) => {
    const data = await ax.post(`/viva/flows/reservation`, {
      flow,
      operation: VivaAPIOperation.ReadPerformanceByAK,
      parameters: { performanceAK },
    });
    return responseMapData(data);
  });
};

/*
 * CheckBasket
 */
export const useCheckBasketMutation = (flow: Flow) => {
  const determinedFlow = useAppSelector(selectCheckoutFlow);

  return useMutation<CheckBasketResponse, BosErrorResponse, CheckBasketRequest>(
    async (parameters) => {
      const data = await ax.post(`/viva/flows/reservation`, {
        flow: determinedFlow,
        operation: VivaAPIOperation.CheckBasket,
        parameters,
      });
      const response = responseMapData(data);

      if (response.ERROR.CODE !== 200) {
        const err = new Error(response.ERROR.TEXT);
        err.name = CheckoutErrors.CheckBasketError;
      }

      return response;
    },
    {
      onSuccess: (response) => {
        if (
          !isBosError(response) &&
          response.ITEMLIST &&
          response.ITEMLIST.ITEM
        ) {
          const selections: CheckoutCartSelectionCapacityGuidPayload[] = [];
          const itemListItems = Array.isArray(response.ITEMLIST.ITEM)
            ? response.ITEMLIST.ITEM
            : [response.ITEMLIST.ITEM];

          itemListItems.forEach((item) => {
            if (item && item.AK && item.CAPACITYGUID) {
              selections.push({
                productAK: item.AK,
                capacityGuid: item.CAPACITYGUID,
              });
            } else {
              console.error('Invalid item structure:', item);
            }
          });
        } else {
          console.error('CheckBasketMutation: Error in response', response);
        }
      },
      onError: (error) => {
        console.error('Error in CheckBasketMutation:', error);
      },
    },
  );
};

/**
 * 9. CloseOrder
 * TODO: This will eventually use the PAYMENTINFOLIST parameters for passing the PAYMENTINFO from Shift4
 * @param parameters {
    saleAK: 'TOR.WKS8.220719.SAL2';
    amount: 51.56;
  }
 * @returns UseQueryResult
 */
export const useCloseOrderMutation = () => {
  const determinedFlow = useAppSelector(selectCheckoutFlow);

  return useMutation<
    CloseOrderResponse<OrderResponse | CityPassCloseOrderResponse>,
    BosErrorResponse,
    CloseOrderRequest
  >(async (parameters) => {
    const data = await ax.post(`/viva/flows/reservation`, {
      flow: determinedFlow,
      operation: VivaAPIOperation.CloseOrder,
      parameters,
    });

    const response = responseMapData(data);

    if (response.ERROR.CODE !== 200) {
      const err = new Error(response.ERROR.TEXT);
      err.name = CheckoutErrors.CloseOrderError;
    }

    return response;
  });
};

export const useAbortSaleMutation = () => {
  const flow = useAppSelector(selectCheckoutFlow);
  return useMutation((parameters: AbortSaleRequest) => {
    return ax
      .post(`/viva/flows/reservation`, {
        flow,
        operation: VivaAPIOperation.AbortSale,
        parameters,
      })
      .then((data) => responseMapData(data));
  });
};

export const useReadExternalPackageByCodeQuery = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  return {
    cityPassBarcodesValidationQuery: useMutation<
      {
        status: { code: number };
        productCode: string;
        ERROR: { CODE: string; TEXT: string };
        PACKAGE: {
          TYPE: string;
          CODE: string;
          PACKAGEINDIVIDUAL?: { CODE: string };
        };
      },
      unknown,
      string
    >((voucherCode) => {
      abortControllerRef.current = new AbortController();

      return ax
        .post<unknown, AxiosResponse<CityPassVoucherValidationResponse>>(
          `/viva/flows/reservation`,
          {
            operation: VivaAPIOperation.ReadExternalPackageByCode,
            parameters: {
              APackageSerial: voucherCode,
              APackageCode: ProductToPackageCode.CPADLTORWEB,
            },
          },
          {
            signal: abortControllerRef.current.signal,
          },
        )
        .then((data) => {
          const {
            data: { ERROR },
          } = data;

          if (ERROR.TEXT !== 'Success') {
            return ax
              .post<unknown, AxiosResponse<CityPassVoucherValidationResponse>>(
                `/viva/flows/reservation`,
                {
                  operation: VivaAPIOperation.ReadExternalPackageByCode,
                  parameters: {
                    APackageSerial: voucherCode,
                    APackageCode: ProductToPackageCode.CPCHDTORWEB,
                  },
                },
              )
              .then((response) => {
                return {
                  ...responseMapData(response),
                  productCode:
                    response.data.ERROR.CODE !== '200'
                      ? null
                      : ProductToPackageCode.CPCHDTORWEB,
                };
              });
          }
          return {
            ...responseMapData(data),
            productCode: ProductToPackageCode.CPADLTORWEB,
          };
        });
    }),
    abortController: abortControllerRef.current,
  };
};

/**
 * 10. ReadOrderByAK
 * @param parameters { orderAK: 'TOR.WKS8.220719.SAL2'; }
 * @returns UseQueryResult
 */
export const useReadOrderByAKQuery = (parameters: ReadOrderByAKRequest) => {
  const flow = useAppSelector(selectFlow);
  return useQuery([VivaAPIOperation.ReadOrderByAK], async () => {
    try {
      const data = await ax.post(`/viva/flows/reservation`, {
        flow: flow === Flow.NONE ? Flow.TOR_GA : flow,
        operation: VivaAPIOperation.ReadOrderByAK,
        parameters,
      });

      return data;
    } catch (err) {
      console.error('Error reading order by ak: ', err);
    }
  });
};

/**
 * @param parameters { saleAK: 'TOR.WKS8.220719.SAL2'; }
 * @returns UseQueryResult
 */
export const usePrintPdfTicketMutation = () => {
  const saleAK = useAppSelector(selectSaleAK);
  const flow = useAppSelector(selectFlow);
  return useMutation(() => {
    return ax.post(`/viva/flows/reservation`, {
      flow,
      operation: VivaAPIOperation.PrintPdfTicket,
      parameters: { saleAK } as PrintPdfTicketRequest,
    });
  });
};

export const useOrderQuery = ({
  orderAK = '',
  flow,
}: {
  orderAK?: string;
  flow: Flow;
}) => {
  return useQuery(
    ['Order', orderAK],
    async (): Promise<OrderResponse> => {
      const data = await ax.post(`/viva/flows/reservation`, {
        flow,
        operation: VivaAPIOperation.ReadOrderByAK,
        parameters: { orderAK },
      });
      return responseMapData(data);
    },
    { enabled: Boolean(orderAK) },
  );
};

export const useValidateCouponQuery = () => {
  const flow = useAppSelector(selectFlow);

  return useMutation(async (parameters: ValidateCouponCodeRequest) => {
    const data = await ax.post(`/viva/flows/reservation`, {
      flow,
      operation: VivaAPIOperation.ValidateCouponCode,
      parameters,
    });
    return responseMapData(data);
  });
};

/**
 * ReadDataProviderByCode
 * @param parameters { dataProviderCode: ADataProviderCode, langCode: ALangCode | undefined }
 * @returns UseQueryResult
 */
export const useDataProviderQuery = ({
  dataProviderCode,
  langCode = LangCode.enUS,
}: ReadDataProviderByCodeRequest) => {
  const flow = useAppSelector(selectFlow);

  return useQuery(
    [VivaAPIOperation.ReadDataProviderByCode, dataProviderCode, langCode],
    () => {
      return ax
        .post(`/viva/masks`, {
          flow,
          operation: VivaAPIOperation.ReadDataProviderByCode,
          parameters: {
            dataProviderCode,
            langCode,
          },
        })
        .then((data) => dataProviderResponseMap(data));
    },
  );
};

/**
 * Viva Search Reservation Mutation
 **/
export const useSearchReservationMutation = () => {
  return useMutation((parameters: SearchReservationRequest) => {
    return ax.post<SearchReservationResponse>('/viva/flows/reservation', {
      operation: VivaAPIOperation.SearchReservation,
      parameters,
    });
  });
};

/**
 * Viva Delete Reservation Mutation
 **/
export const useDeleteReservationMutation = () => {
  return useMutation((parameters: DeleteReservationRequest) => {
    return ax.post<DeleteReservationResponse>('/viva/flows/reservation', {
      operation: VivaAPIOperation.DeleteReservation,
      parameters,
    });
  });
};

/**
 * Shift4 Payment/Transaction Processing Mutation
 */
export const usePaymentMutation = () => {
  return useMutation((transaction: SaleRequest) => {
    return new Promise<Shift4SaleResponse | Shift4ErrorResponse>((resolve) => {
      axios
        .post('/api/shift4/transactions', transaction)
        .then((response) => resolve(response.data))
        .catch((error) => console.log(error));
    });
  });
};

/**
 * Shift4 Void Transaction Mutation
 */
export const useVoidTransactionMutation = () => {
  return useMutation(({ invoice, flow }: { invoice: string; flow: Flow }) => {
    return new Promise<Shift4VoidResponse | Shift4ErrorResponse>((resolve) => {
      axios
        .post('/api/shift4/transactions/void', { invoice, flow })
        .then((response) => resolve(response.data))
        .catch((error) => resolve(error));
    });
  });
};

/**
 * Shift4 Refund Transaction Mutation
 **/
export const useRefundTransactionMutation = () => {
  return useMutation((transaction: RefundRequest) => {
    return axios.post<Shift4RefundResponse>(
      '/api/shift4/transactions/refund',
      transaction,
    );
  });
};
